import React, { useEffect, useRef } from 'react';
import { RotateCcw, X } from 'lucide-react';

/**
 * "Your selection" on the public booking page (#758): one removable chip per
 * choice the guest made — Coach, Date, Time, in that order — and Reset.
 *
 * `coach`, `date` and `slot` say which chips exist; `labels.{coach,date,time}`
 * are their visible text. `onRemove(kind)` removes one ('coach' | 'date' |
 * 'time'); the page decides the knock-on (removing the date clears the time).
 * Reset is natively `disabled` when nothing is selected.
 *
 * Focus is never lost when a chip's button unmounts under the keyboard: it
 * moves to the next chip still shown after the removed one, else to Reset.
 * When the last chip goes, Reset is disabled (and cannot hold focus), so focus
 * lands on the bar itself (`tabIndex=-1`, labelled "Your selection").
 */
const KINDS = ['coach', 'date', 'time'];
const NAMES = { coach: 'Coach', date: 'Date', time: 'Time' };

export default function SelectionBar({ coach, date, slot, labels = {}, onRemove, onReset }) {
    const present = { coach: !!coach, date: !!date, time: !!slot };
    const any = KINDS.some((k) => present[k]);
    const chipRefs = useRef({});
    const resetRef = useRef(null);
    const barRef = useRef(null);
    const pendingFocus = useRef(null);   // index of the chip just removed

    useEffect(() => {
        if (pendingFocus.current == null) return;
        const from = pendingFocus.current;
        pendingFocus.current = null;
        const next = KINDS.slice(from + 1).find((k) => present[k] && chipRefs.current[k]);
        const reset = resetRef.current;
        const target = next ? chipRefs.current[next] : (reset && !reset.disabled ? reset : barRef.current);
        if (target) target.focus();
    });

    const remove = (kind) => {
        pendingFocus.current = KINDS.indexOf(kind);
        if (onRemove) onRemove(kind);
    };

    return (
        <div ref={barRef} tabIndex={-1} role="group" aria-label="Your selection"
            className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-stone-100 bg-stone-50/60 focus:outline-none"
            data-testid="selection-bar">
            <span aria-hidden="true" className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mr-1">Your selection</span>
            {!any && <span className="text-xs text-stone-400">Pick a coach, a date or a time, in any order.</span>}
            {KINDS.filter((k) => present[k]).map((k) => (
                <button key={k} type="button" data-testid={`chip-${k}`}
                    ref={(el) => { chipRefs.current[k] = el; }}
                    aria-label={`Remove ${k}: ${labels[k] || ''}`.trim()}
                    onClick={() => remove(k)}
                    className="inline-flex items-center gap-1.5 min-h-8 max-w-full rounded-full border border-stone-300 bg-white pl-3 pr-2 py-1 text-xs text-stone-700 hover:border-stone-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">
                    <span className="text-stone-400">{NAMES[k]}:</span>
                    <span className="font-medium truncate">{labels[k]}</span>
                    <X className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" aria-hidden="true" />
                </button>
            ))}
            <button type="button" ref={resetRef} data-testid="selection-reset" disabled={!any} onClick={onReset}
                className="ml-auto inline-flex items-center gap-1 min-h-8 rounded-md px-2 text-xs font-medium text-stone-600 hover:text-stone-900 disabled:text-stone-300 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Reset
            </button>
        </div>
    );
}
